import { theme as antdTheme } from 'antd'
import BRAND from 'brand'

/**
 * Kit palette, carried over from the old default.less / dark.less files.
 *
 * antd 4 was themed by overriding Less variables at build time. antd 5 dropped
 * that entirely in favour of runtime design tokens, so those .less files became
 * inert and the palette now has to reach antd through ConfigProvider instead.
 */
export const KIT_COLORS = {
  white: '#ffffff',
  black: '#141322',
  gray1: '#f0f2f4',
  gray2: '#e4e9f0',
  gray3: '#d9dee9',
  gray4: '#c8c4db',
  gray6: '#595c97',
  grayDark1: '#aeaee0',
  grayDark2: '#7575a3',
  grayDark4: '#232135',
  blue: '#4b7cf3',
  blueLight: '#42baf9',
  red: '#f5222e',
  green: '#41b883',
  orange: '#faad15',
}

/**
 * Picks a legible foreground for a filled background.
 *
 * The brand colour is user-configurable, so a fixed text colour would be
 * unreadable for half the possible choices -- the default (black) needs white
 * text, a pale one needs dark text.
 */
export const readableTextOn = color => {
  const hex = String(color).replace('#', '')
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map(char => char + char)
          .join('')
      : hex

  if (full.length !== 6) {
    return KIT_COLORS.white
  }

  const int = parseInt(full, 16)
  if (Number.isNaN(int)) {
    return KIT_COLORS.white
  }

  /* eslint-disable no-bitwise */
  const r = (int >> 16) & 255
  const g = (int >> 8) & 255
  const b = int & 255
  /* eslint-enable no-bitwise */

  // perceived brightness (sRGB weighting)
  const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return brightness > 0.6 ? KIT_COLORS.gray6 : KIT_COLORS.white
}

/**
 * A translucent wash of a colour, for fills that should read as a highlight
 * rather than a solid block. Falls back to the colour itself if it isn't a
 * parseable hex.
 */
export const withAlpha = (color, alpha) => {
  const hex = String(color).replace('#', '')
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map(char => char + char)
          .join('')
      : hex

  const int = parseInt(full, 16)
  if (full.length !== 6 || Number.isNaN(int)) {
    return color
  }

  /* eslint-disable no-bitwise */
  const r = (int >> 16) & 255
  const g = (int >> 8) & 255
  const b = int & 255
  /* eslint-enable no-bitwise */

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Builds the antd 5 theme config from the app's redux settings.
 *
 * `primaryColor` is the user-configurable colour already used by the app's own
 * SCSS (via the --kit-color-primary custom property); feeding it in here is what
 * makes the sidebar colour picker affect antd components again.
 */
export default function buildAntdTheme({ theme, primaryColor } = {}) {
  const isDark = theme === 'dark'
  const primary = primaryColor || KIT_COLORS.blue
  const selectedColor = readableTextOn(primary)

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      // antd 5 injects component styles at runtime, after the bundled CSS, and
      // sets font-family on the components themselves -- so without this the
      // antd default (-apple-system first) beats the app's Roboto. Tracks the
      // same custom property kit/core/css/core.scss styles the app with.
      fontFamily: "var(--kit-font-family, 'Roboto', sans-serif)",
      colorPrimary: primary,
      colorInfo: isDark ? KIT_COLORS.blueLight : KIT_COLORS.blue,
      colorSuccess: KIT_COLORS.green,
      colorWarning: KIT_COLORS.orange,
      colorError: KIT_COLORS.red,
      // antd derives the "hovered row" fill from colorPrimary. The brand colour
      // is black, which has no saturation, so that derivation produces a very
      // dark fill while the text stays dark -- pin it to a readable neutral.
      controlItemBgHover: isDark ? KIT_COLORS.grayDark4 : KIT_COLORS.gray1,
      // Tooltips take their fill from colorBgSpotlight, which antd derives from
      // colorTextBase -- and ours is a blue-grey, which tinted them blue. Pin it
      // to the palette's black; antd pairs it with white text automatically.
      colorBgSpotlight: KIT_COLORS.black,
      ...(isDark
        ? {
            colorBgBase: KIT_COLORS.black,
            colorTextBase: KIT_COLORS.grayDark1,
          }
        : {
            colorTextBase: BRAND.colors.text || KIT_COLORS.gray6,
            colorBgBase: BRAND.colors.background || KIT_COLORS.white,
            colorBorder: KIT_COLORS.gray2,
          }),
    },
    components: {
      // Selected options get the same light brand wash as the table filter
      // menus below -- solid brand fills read as heavy black slabs when several
      // adjacent options are selected. Square corners make stacked selections
      // merge into one block; the label takes the brand colour for emphasis,
      // matching antd's own selected-label treatment.
      //
      // controlItemBgActiveHover paints the option that is *both* selected and
      // active (the keyboard/hover-highlighted one). antd derives it from
      // colorPrimaryBgHover, which for a black brand resolves to a near-black
      // #333 -- the dark slab over the highlighted option. Pin it to a slightly
      // stronger wash than the resting selection, same as the Dropdown below.
      Cascader: {
        optionSelectedBg: withAlpha(primary, 0.1),
        optionSelectedColor: primary,
        controlItemBgActiveHover: withAlpha(primary, 0.16),
        borderRadiusSM: 0,
      },
      Select: {
        optionSelectedBg: withAlpha(primary, 0.1),
        optionSelectedColor: primary,
        controlItemBgActiveHover: withAlpha(primary, 0.16),
        borderRadiusSM: 0,
      },
      // The collapsed sidebar renders submenus in a portal, out of reach of the
      // scoped rules in MenuLeft/style.module.scss, so the selected-item styling
      // has to be restated as tokens. Inside the sider those rules use
      // !important and still win, leaving these to cover only the popup.
      Menu: {
        itemSelectedBg: primary,
        itemSelectedColor: selectedColor,
      },
      // Date/time pickers (TimePicker and RangePicker are built from DatePicker,
      // so this covers all three). Two spots go dark on a black brand:
      //   - controlItemBgActive fills the selected time-column cell; antd
      //     derives it from colorPrimaryBg, a dark #404040, under the dark body
      //     text -- dark-on-dark and unreadable.
      //   - cellActiveWithRangeBg fills every in-range day of a RangePicker.
      //     antd derives it from controlItemBgActive *inside* the component's
      //     token step, before this override reaches it, so it has to be pinned
      //     directly or a whole selected range renders as a wall of black slabs.
      // Both take the same light brand wash as the selects. (The range
      // endpoints / single selected date are filled with colorPrimary and
      // paired with white text by antd, so they stay readable and are left
      // alone.)
      DatePicker: {
        controlItemBgActive: withAlpha(primary, 0.1),
        cellActiveWithRangeBg: withAlpha(primary, 0.1),
      },
      // Table filter menus (and any selectable dropdown menu) paint selected
      // items with controlItemBgActive -- which antd derives from colorPrimary,
      // so a black brand yields a murky near-black fill under dark text. A
      // solid brand fill looked too heavy in rows of checkboxes; use a light
      // wash of the brand colour instead (the old portal's look, minus its
      // off-brand blue), with square corners so stacked selections read as one
      // block instead of a pile of rounded slabs. The label keeps antd's
      // selected colour, which is the brand colour itself.
      Dropdown: {
        controlItemBgActive: withAlpha(primary, 0.1),
        controlItemBgActiveHover: withAlpha(primary, 0.16),
        borderRadiusSM: 0,
      },
    },
  }
}
